import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { apiFetch } from '../api/client';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
        padding: '1rem',
      }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 400 }}>
          <p style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>
            Link non valido. Richiedi un nuovo link di reset.
          </p>
          <Link to="/forgot-password" style={{ color: 'var(--color-primary)', fontSize: 'var(--text-sm)', textDecoration: 'none' }}>
            Richiedi nuovo link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
      navigate('/login', { state: { resetSuccess: true } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore durante il reset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: '1rem',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 60% 50% at 50% 0%, oklch(0.45 0.07 195 / 0.12), transparent)',
      }} />

      <div style={{
        width: '100%',
        maxWidth: 400,
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        position: 'relative',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
            <rect width="52" height="52" rx="14" fill="var(--color-primary)" />
            <text x="7" y="40" fontFamily="Satoshi, system-ui, sans-serif" fontWeight="700" fontSize="34" fill="white">LC</text>
          </svg>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontFamily: 'Satoshi, system-ui, sans-serif', fontWeight: 700,
              fontSize: '1.75rem',
              color: 'var(--color-text)',
              marginBottom: '0.25rem',
            }}>Nuova password</h1>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Scegli una nuova password per il tuo account
            </p>
          </div>
        </div>

        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-container)',
          padding: '2rem',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              <label htmlFor="newPassword" style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)' }}>
                Nuova password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="newPassword"
                  type={showPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  autoComplete="new-password"
                  placeholder="Minimo 6 caratteri"
                  style={{
                    background: 'var(--glass-bg-card)',
                    border: `1px solid ${error ? 'var(--color-error)' : 'rgba(255,255,255,0.10)'}`,
                    borderRadius: 'var(--radius-card)',
                    color: 'var(--color-text)',
                    fontSize: 'var(--text-sm)',
                    padding: '0.625rem 2.75rem 0.625rem 0.875rem',
                    width: '100%',
                    outline: 'none',
                    transition: 'border-color var(--transition-interactive)',
                  }}
                  onFocus={e => (e.target.style.borderColor = 'var(--color-primary)')}
                  onBlur={e => (e.target.style.borderColor = error ? 'var(--color-error)' : 'rgba(255,255,255,0.10)')}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
                  style={{
                    position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--color-text-faint)', padding: '0.25rem',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                background: 'var(--color-error-highlight)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-card)',
                padding: '0.625rem 0.875rem',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-error)',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                background: 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius-card)',
                color: '#fff',
                fontSize: 'var(--text-base)',
                fontWeight: 600,
                padding: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'all var(--transition-interactive)',
                marginTop: '0.25rem',
              }}
              onMouseEnter={e => { if (!loading) (e.currentTarget.style.background = 'var(--color-primary-hover)'); }}
              onMouseLeave={e => { (e.currentTarget.style.background = 'var(--color-primary)'); }}
            >
              {loading ? 'Salvataggio…' : 'Salva nuova password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
